import React from "react";
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    Divider,
    Button,
    Stack,
    Chip,
} from "@mui/material";
import { Description, Link, Phone, Message } from "@mui/icons-material";
import { WB_SX } from "../../../ui/ux/uiTokens";

const TemplateDetailsPreview = ({
    formattedTemplate,
}) => {
    if (!formattedTemplate) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No template data available</Typography>
            </Paper>
        );
    }

    const headerComponent = formattedTemplate.header 
        ? { 
            type: "HEADER", 
            format: formattedTemplate.header.type || formattedTemplate.header.format || "TEXT",
            text: formattedTemplate.header.type === "TEXT" ? formattedTemplate.header.text : null
          }
        : null;
    
    const bodyComponent = formattedTemplate.body 
        ? { type: "BODY", text: formattedTemplate.body.text } 
        : null;
    
    const footerComponent = formattedTemplate.footer 
        ? { type: "FOOTER", text: formattedTemplate.footer.text } 
        : null;
    
    const buttonsComponent = formattedTemplate.buttons && formattedTemplate.buttons.length > 0
        ? { type: "BUTTONS", buttons: formattedTemplate.buttons }
        : null;

    // WhatsApp background pattern
    const whatsappBgPattern = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAAdgAAAHYBTnsmCAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAAAnSURBVHic7cEBDQAAAMIg+6c2BL4mAAAAAAAAAAAAAAAAAAD4B7a4AAFj8b8zAAAAAElFTkSuQmCC")`;

    return (
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                ...WB_SX.card,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* WhatsApp Header */}
            <Box
                sx={{
                    backgroundColor: "#075e54",
                    color: "white",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: "#25d366",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                    }}
                >
                    WA
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        WhatsApp Business
                    </Typography>
                </Box>
            </Box>

            {/* WhatsApp Chat Area */}
            <Box
                sx={{
                    backgroundColor: "#e5ddd5",
                    backgroundImage: whatsappBgPattern,
                    minHeight: 300,
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1,
                }}
            >
                {/* Template Message Bubble - Received */}
                <Card
                    sx={{
                        maxWidth: "75%",
                        mr: "auto",
                        ml: 0,
                        backgroundColor: "#ffffff",
                        borderRadius: "0 7.5px 7.5px 7.5px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        border: "1px solid #e5e5e5",
                    }}
                >
                    <CardContent sx={{ p: 1, "&:last-child": { pb: 1.5 } }}>
                        {/* Header (Text) */}
                        {headerComponent?.format === "TEXT" && formattedTemplate.header?.text && (
                            <>
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{ mb: 1, color: "#075e54" }}
                                >
                                    {formattedTemplate.header.text}
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                            </>
                        )}

                        {/* Header (Media) */}
                        {headerComponent &&
                            ["IMAGE", "VIDEO", "DOCUMENT"].includes(
                                headerComponent.format
                            ) && (
                                <Box sx={{ mb: 1 }}>
                                    {formattedTemplate.headerMediaPreviewUrl ? (
                                        <Box>
                                            {headerComponent.format === "IMAGE" && (
                                                <Box
                                                    component="img"
                                                    src={formattedTemplate.headerMediaPreviewUrl}
                                                    alt="Preview"
                                                    sx={{
                                                        width: "100%",
                                                        maxHeight: 200,
                                                        borderRadius: 1,
                                                        display: "block",
                                                        objectFit: "contain",
                                                    }}
                                                    onError={(e) => {
                                                        console.error("Error loading image:", formattedTemplate.headerMediaPreviewUrl);
                                                        e.target.style.display = "none";
                                                    }}
                                                />
                                            )}
                                            {headerComponent.format === "VIDEO" && (
                                                <Box
                                                    component="video"
                                                    src={formattedTemplate.headerMediaPreviewUrl}
                                                    controls
                                                    sx={{
                                                        width: "100%",
                                                        maxHeight: 200,
                                                        borderRadius: 1,
                                                        display: "block",
                                                    }}
                                                />
                                            )}
                                            {headerComponent.format === "DOCUMENT" && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        p: 1.5,
                                                        backgroundColor: "#f5f5f5",
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Description fontSize="large" color="primary" />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            Document
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Ready to send
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            Media will appear here
                                        </Typography>
                                    )}
                                </Box>
                            )}

                        {/* Body */}
                        {formattedTemplate.body?.text && (
                            <Typography
                                variant="body2"
                                sx={{
                                    lineHeight: 1.5,
                                    whiteSpace: "pre-wrap",
                                    mb: footerComponent?.text || buttonsComponent?.buttons ? 1 : 0,
                                }}
                            >
                                {formattedTemplate.body.text}
                            </Typography>
                        )}

                        {/* Footer */}
                        {footerComponent?.text && (
                            <>
                                {(formattedTemplate.body?.text || headerComponent) && <Divider sx={{ my: 1 }} />}
                                <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                                >
                                    {footerComponent.text}
                                </Typography>
                            </>
                        )}

                        {/* Buttons */}
                        {buttonsComponent?.buttons && buttonsComponent.buttons.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                                <Divider sx={{ mb: 1.5 }} />
                                <Stack spacing={1}>
                                    {buttonsComponent.buttons.map((button, index) => {
                                        const getButtonIcon = () => {
                                            const buttonType = button.type || "QUICK_REPLY";
                                            switch (buttonType) {
                                                case "URL":
                                                    return <Link fontSize="small" />;
                                                case "PHONE_NUMBER":
                                                    return <Phone fontSize="small" />;
                                                case "QUICK_REPLY":
                                                default:
                                                    return <Message fontSize="small" />;
                                            }
                                        };

                                        return (
                                            <Button
                                                key={index}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                startIcon={getButtonIcon()}
                                                sx={{
                                                    borderColor: "#25d366",
                                                    color: "#25d366",
                                                    textTransform: "none",
                                                    fontSize: "0.75rem",
                                                    justifyContent: "center",
                                                    "&:hover": {
                                                        borderColor: "#128c7e",
                                                        backgroundColor: "rgba(37, 211, 102, 0.1)",
                                                    },
                                                }}
                                            >
                                                {button.text || button.type}
                                            </Button>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        )}
                    </CardContent>

                    {/* Message Time */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            px: 1.5,
                            pb: 1,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.7rem",
                            }}
                        >
                            {new Date().toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </Typography>
                    </Box>
                </Card>

                {/* Info Message */}
                <Card
                    sx={{
                        maxWidth: "80%",
                        ml: "auto",
                        backgroundColor: "#dcf8c6",
                        borderRadius: "7.5px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    }}
                >
                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Typography variant="caption" color="text.secondary">
                            Preview of your template message
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* WhatsApp Footer */}
            <Box
                sx={{
                    backgroundColor: "#f0f0f0",
                    p: 1.5,
                    textAlign: "center",
                    borderTop: "1px solid #e5e5e5",
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    Template message • No replies
                </Typography>
            </Box>

            {/* Template Info */}
            {(formattedTemplate.category || formattedTemplate.language) && (
                <Box sx={{ p: 1.5, backgroundColor: "#f9f9f9", display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {formattedTemplate.category && (
                        <Chip
                            label={`Category: ${formattedTemplate.category}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                    {formattedTemplate.language && (
                        <Chip
                            label={`Language: ${formattedTemplate.language}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                    {formattedTemplate.status && (
                        <Chip
                            label={`Status: ${formattedTemplate.status}`}
                            size="small"
                            color={
                                formattedTemplate.status === 'APPROVED' 
                                    ? 'success' 
                                    : formattedTemplate.status === 'PENDING'
                                    ? 'warning'
                                    : formattedTemplate.status === 'REJECTED'
                                    ? 'error'
                                    : 'default'
                            }
                            variant="outlined"
                        />
                    )}
                </Box>
            )}
        </Paper>
    );
};

export default TemplateDetailsPreview;

